import { observable, action, computed, makeObservable, runInAction } from "mobx";
import { AgendaItem } from "src/types";
import { RootStore } from "src/stores/RootStore";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { FORM_STATUS } from "src/utils/Form";
import { getRoleKey } from "src/network/User";
import { NetworkConfig } from "src/network/NetworkConfig";
import { GetAllAssemblies } from "src/api/generated/GetAllAssemblies";
import {
    DELETE_ASSEMBLY_BY_ID,
    GET_ALL_ASSEMBLIES,
    GET_ALL_ASSEMBLIES_BY_COWWNER_REALESTATEID,
    GET_ASSEMBLIES_BY_REALESTATEID,
    GET_ASSEMBLY_BY_ID,
    INSERT_ASSEMBLY_REALESTATE_MANAGER,
    SUBCRIPTION_HAS_ASSEMBLY_LIVEMODE_CHANGED,
    SUBCRIPTION_IS_ASSEMBLY_ASSIGNED_TO_REALESTATE,
    UPDATE_ASSEMBLY_LIVEMODE,
    UPDATE_ASSEMBLY_STATE,
    UPSERT_ASSEMBLIES,
    UPSERT_ASSEMBLY_HEADER_FOOTER
} from "src/api/assembly";
import {
    IsAssemblyAssignedToRealestate,
    IsAssemblyAssignedToRealestateVariables
} from "src/api/generated/IsAssemblyAssignedToRealestate";
import {
    GetAssemblyByAssemblyId,
    GetAssemblyByAssemblyId_assemblyitems,
    GetAssemblyByAssemblyIdVariables
} from "src/api/generated/GetAssemblyByAssemblyId";
import i18next from "i18next";
import { Subscription } from "zen-observable-ts";
import { RealestateCondominium } from "src/stores/RealestateStore";
import { MessageType } from "src/components/notifications/Notifier";
import { DeleteAssemblyById, DeleteAssemblyByIdVariables } from "src/api/generated/DeleteAssemblyById";
import {
    cond_agendaitemtemplates_insert_input,
    cond_assemblyrealestates_insert_input
} from "src/api/generated/globalTypes";
import { UpsertAssembly, UpsertAssemblyVariables } from "src/api/generated/UpsertAssembly";
import {
    InsertAssemblyRealestateManager,
    InsertAssemblyRealestateManagerVariables
} from "src/api/generated/InsertAssemblyRealestateManager";
import { UpdateAssemblyState, UpdateAssemblyStateVariables } from "src/api/generated/UpdateAssemblyState";
import Axios from "src/network/Axios";
import { UpdateAssemblyLiveMode, UpdateAssemblyLiveModeVariables } from "src/api/generated/UpdateAssemblyLiveMode";
import {
    HasAssemblyLiveModeChanged,
    HasAssemblyLiveModeChangedVariables
} from "src/api/generated/HasAssemblyLiveModeChanged";
import {
    UpsertAssemblyHeaderFooter,
    UpsertAssemblyHeaderFooterVariables
} from "src/api/generated/UpsertAssemblyHeaderFooter";
import { ENUM_TYPE } from "src/stores/EnumerationStore";
import { getLang } from "src/utils/language";

export enum ASSEMBLY_STATE {
    PREPARING = "preparing",
    ACTIVE = "active",
    AGENDA_PUBLISHED = "agenda_published",
    DATE_PUBLISHED = "date_published",
    PROTOCOL_PUBLISHED = "protocol_published",
    CLOSED = "closed"
}

export interface AssemblyDetailEditFormFields {
    id: string;
    remarks: string;
    date: string;
    state: ASSEMBLY_STATE;
    zip: string;
    city: string;
    street: string;
    housenumber: string;
    title: string;
    locationdetail: string;
    motiondeadline?: string | null;
    livemode: boolean;
    header: string;
    footer: string;
}

export enum LOADING_TYPE {
    NONE,
    ASSEMBLY,
    SEARCHING_REALESTATES,
    SAVING_ASSEMBLY,
    CHECK_ASSEMBLY_CAN_BE_COMPLETED,
    CLOSING_ASSEMBLY
}

export enum ASSEMBLY_SEARCH_FILTER_TYPE {
    REALESTATE,
    UNIT
}

export interface IASSEMBLYSearchFilter {
    byRealestate: boolean;
}
interface House {
    zip: string;
    city: string;
    street: string;
    housenumber: string;
}
export interface Realestate {
    id: string;
    number: string;
    name: string;
    houses?: House[];
    consolidationid?: string | null;
}

export interface AssemblyRealestate {
    id: string;
    realestate: Realestate;
    // true when sourced from v_assemblyrealestates (closed assemblies) instead of the live assemblyrealestates table
    isReadOnly?: boolean;
}

export interface Assembly {
    id: string;
    date: string;
    state: ASSEMBLY_STATE;
    street: string | null;
    housenumber: string | null;
    zip: string | null;
    city: string | null;
    title: string;
    locationdetail: string | null;
    publishdate: string | null;
    motiondeadline: string;
    assemblyevents?: string | null;
    summaryassemblyfilesCount: number;
    assemblyrealestates: AssemblyRealestate[];
    protocolauthor: string | null;
    protocoldate: string | null;
    protocolplace: string | null;
    livemode: boolean;
    header?: string;
    footer?: string;
}

export interface AssemblyState {
    id: string;
    key: number;
    label: string;
}

export class AssemblyStore {
    rootStore: RootStore;

    assemblies: Assembly[] = [];
    assemblyStates: AssemblyState[] = [];
    currentAssembly: Assembly | undefined;
    hasCurrentAssemblyLoaded: boolean = false;
    currentAssemblyTitle: string = "";
    isNewAssembly: boolean = false;

    isAddNewAgendaItem: boolean = false;
    resetAgendaItems: boolean = false;
    agendaItemsHaveChanged: boolean = false;
    originalAgendaItems: AgendaItem[] = [];
    agendaItems: AgendaItem[] = [];
    unsavedAgendaItems?: AgendaItem[] = [];
    isAssemblyInsertedEventLoaded: boolean = true;
    isAgendaItemFilesExists: boolean = false;
    isCopyAgendaItemsActive: boolean = false;
    isAddingDocumentsDisplayed: boolean = false;
    focusedAgendaItemIdText: string = "";
    deletedAgendaItemIds: string[] = [];
    ifAgendaItemFocusOrDocumentExists: boolean = false;
    assemblyId: string = "";
    insertAssemblyAgendaitems: any;
    insertAgendaitemsTemplates: any;
    showRefetchAssemblyData: boolean = false;
    isAddNewChildAgendaItem: boolean = false;
    selectedCopyAssemblyAgendaOption: number = 0;
    isAssemblyFilesExists: boolean = false;
    oldAssemblyWithGeneralDocuments: boolean = false;
    isAgendaItemTemplate: boolean = false;
    assembliesEditFormStatus: FORM_STATUS = FORM_STATUS.NONE;
    assembliesEditFormHasDirtyFields: boolean = false;
    isAssemblyPDFExists: boolean = false;
    isNewAssemblyPDFExists: boolean = true;
    hasActiveCoowners: boolean = false;
    isNewDescription: string = "";
    focusedAgendaItemIdDescription: string = "";
    isEditing: boolean = false;
    focusedAgendaItem?: AgendaItem;
    agendaItemsExists: boolean = false;
    isManagerRealestateAssemblyRoute: boolean = false;

    NEW_ASSEMBLY_ID = "new-assembly";
    loadingType: LOADING_TYPE = LOADING_TYPE.NONE;

    // assembly realestates
    isAddAssemblyRealestateModalDisplayed = false;
    currentAssemblyRealestates: AssemblyRealestate[] = [];
    formattedRealestates: RealestateCondominium[] = [];
    currentAssemblyRealestatesHaveChanged: boolean = false;

    isAssemblyGetNotified: boolean = false;

    isAssemblyAssignedToRealestate: boolean = true;
    isAssemblyAssignedToRealestateSubscription?: Subscription;

    isCloseLiveModeTabActive: boolean = false;
    isAttendanceLiveModeTabActive: boolean = false;
    hasAssemblyLiveModeChangedSubscription?: Subscription;
    assembyLiveModeHasStopped: boolean = false;
    assemblyIsPreparing: boolean = false;
    isSuccessfullyDeletedAssembly: boolean = false;
    isFreetextHeaderExpanded: boolean = false;
    isFreetextFooterExpanded: boolean = false;

    error?: {
        title: string;
    } = undefined;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            isAddNewAgendaItem: observable,
            resetAgendaItems: observable,
            agendaItemsHaveChanged: observable,
            originalAgendaItems: observable,
            agendaItems: observable,
            unsavedAgendaItems: observable,
            isAssemblyInsertedEventLoaded: observable,
            isAgendaItemFilesExists: observable,
            isCopyAgendaItemsActive: observable,
            isAddingDocumentsDisplayed: observable,
            focusedAgendaItemIdText: observable,
            deletedAgendaItemIds: observable,
            ifAgendaItemFocusOrDocumentExists: observable,
            assemblyId: observable,
            insertAssemblyAgendaitems: observable,
            insertAgendaitemsTemplates: observable,
            showRefetchAssemblyData: observable,
            isAddNewChildAgendaItem: observable,
            selectedCopyAssemblyAgendaOption: observable,
            isAssemblyFilesExists: observable,
            oldAssemblyWithGeneralDocuments: observable,
            isAgendaItemTemplate: observable,
            assembliesEditFormStatus: observable,
            assembliesEditFormHasDirtyFields: observable,
            isAssemblyPDFExists: observable,
            isNewAssemblyPDFExists: observable,
            hasActiveCoowners: observable,
            isNewDescription: observable,
            focusedAgendaItemIdDescription: observable,
            currentAssembly: observable,
            currentAssemblyTitle: observable,
            isNewAssembly: observable,
            isEditing: observable,
            loadingType: observable,
            error: observable,
            focusedAgendaItem: observable,
            hasCurrentAssemblyLoaded: observable,
            isAddAssemblyRealestateModalDisplayed: observable,
            currentAssemblyRealestates: observable,
            agendaItemsExists: observable,
            formattedRealestates: observable,
            currentAssemblyRealestatesHaveChanged: observable,
            isAssemblyAssignedToRealestate: observable,
            isManagerRealestateAssemblyRoute: observable,
            isAssemblyAssignedToRealestateSubscription: observable,
            isSuccessfullyDeletedAssembly: observable,
            isFreetextHeaderExpanded: observable,
            isFreetextFooterExpanded: observable,

            setLoadingError: action,
            setLoadingType: action,
            loadCurrentAssembly: action,
            setIsEditing: action,
            setCurrentAssembly: action,
            setCurrentAssemblyTitle: action,
            setCurrentAssemblyLiveMode: action,
            setIsNewAssembly: action,
            setIsAssemblyInsertedEventLoaded: action,
            setIsAddNewAgendaItem: action,
            setIsAddNewChildAgendaItem: action,
            setResetAgendaItems: action,
            updateSortAgendaItemList: action,
            setOriginalAgendaItems: action,
            setCurrentAgendaItems: action,
            setUnsavedAgendaItems: action,
            setAgendaItemsHaveChanged: action,
            setIsAgendaItemFilesExists: action,
            setIsCopyAgendaItemsActive: action,
            setIsAddingDocumentsDisplayed: action,
            setFocusedAgendaItemIdText: action,
            setFocusedAgendaItemIdDescription: action,
            addDeletedAgendaItemId: action,
            setIfAgendaItemFocusOrDocumentExists: action,
            setAssemblyId: action,
            createInsertAssemblyAgendaitems: action,
            createInsertAgendaitemsTemplate: action,
            setShowRefetchAssemblyData: action,
            setIsAgendaItemTemplate: action,
            setSelectedCopyAssemblyAgendaOption: action,
            setIsAssemblyFilesExists: action,
            setOldAssemblyWithGeneralDocuments: action,
            setAssembliesEditFormStatus: action,
            setAssembliesEditFormHasDirtyFields: action,
            setIsAssemblyPDFExists: action,
            setIsNewAssemblyPDFExists: action,
            generateAssemblyRealestatePDF: action,
            setHasActiveCoowners: action,
            setIsNewDescription: action,
            setFocusedAgendaItem: action,
            setIsAddAssemblyRealestateModalDisplayed: action,
            loadCurrentAssemblyRealestates: action,
            addCurrentAssemblyRealestates: action,
            removeCurrentAssemblyRealestates: action,
            setCurrentAssemblyRealestates: action,
            setAgendaItemsExists: action,
            setFormattedRealestates: action,
            setCurrentAssemblyRealestatesHaveChanged: action,
            setIsAssemblyAssignedToRealestate: action,
            setIsManagerRealestateAssemblyRoute: action,
            setIsAssemblyAssignedToRealestateSubscription: action,
            setIsSuccessfullyDeletedAssembly: action,
            setIsFreetextHeaderExpanded: action,
            setIsFreetextFooterExpanded: action,
            saveAssemblyHeaderFooter: action,
            addClosingNoteToConclusion: action,
            recordClosingTimeAndCloseLiveMode: action,

            assemblies: observable,
            assemblyStates: observable,

            isAssemblyGetNotified: observable,
            isCloseLiveModeTabActive: observable,
            isAttendanceLiveModeTabActive: observable,
            hasAssemblyLiveModeChangedSubscription: observable,
            assembyLiveModeHasStopped: observable,
            assemblyIsPreparing: observable,

            deleteAssemblyById: action,
            saveCurrentAssembly: action,
            loadAllAssemblies: action,
            reset: action,
            setHasCurrentAssemblyLoaded: action,
            printStatusMessage: action,

            updateAssemblyState: action,
            setAssemblyState: action,
            updateAssemblyLiveMode: action,

            notifyAssembly: action,
            setIsAssemblyGetNotified: action,
            setIsCloseLiveModeTabActive: action,
            setIsAttendanceLiveModeTabActive: action,
            sethasAssemblyLiveModeChangedSubscription: action,
            setAssembyLiveModeHasStopped: action,
            setAssemblyIsPreparing: action,

            isAssemblyReadOnly: computed
        });
    }

    get isAssemblyReadOnly(): boolean {
        return (
            this.currentAssembly?.state === ASSEMBLY_STATE.PROTOCOL_PUBLISHED ||
            this.currentAssembly?.state === ASSEMBLY_STATE.CLOSED
        );
    }

    printStatusMessage = (message: string, messageType: MessageType) => {
        this.rootStore.uiStore.enqueueSnackbar({
            content: message,
            options: {
                type: messageType,
                autoClose: messageType === MessageType.ERROR ? false : 3000
            }
        });
    };

    setIsAssemblyAssignedToRealestateSubscription = (
        isAssemblyAssignedToRealestateSubscription: Subscription | undefined
    ) => {
        this.isAssemblyAssignedToRealestateSubscription = isAssemblyAssignedToRealestateSubscription;
    };

    sethasAssemblyLiveModeChangedSubscription = (hasAssemblyLiveModeChangedSubscription: Subscription | undefined) => {
        this.hasAssemblyLiveModeChangedSubscription = hasAssemblyLiveModeChangedSubscription;
    };

    setAssembyLiveModeHasStopped = (assembyLiveModeHasStopped: boolean) => {
        this.assembyLiveModeHasStopped = assembyLiveModeHasStopped;
    };

    setAssemblyIsPreparing = (assemblyIsPreparing: boolean) => {
        this.assemblyIsPreparing = assemblyIsPreparing;
    };

    onIsAssemblyAssignedToRealestateSubscriptionDataReceived = (counter: number) => {
        this.setIsAssemblyAssignedToRealestate(counter > 0);
    };

    mapState(state: number): ASSEMBLY_STATE {
        switch (state) {
            case 0:
                return ASSEMBLY_STATE.PREPARING;
            case 1:
                return ASSEMBLY_STATE.AGENDA_PUBLISHED;
            case 5:
                return ASSEMBLY_STATE.DATE_PUBLISHED;
            case 6:
                return ASSEMBLY_STATE.PROTOCOL_PUBLISHED;
            case 9:
                return ASSEMBLY_STATE.CLOSED;
            default:
                throw new Error(`Unknown state: ${state}`);
        }
    }

    mapStateToInteger(state: ASSEMBLY_STATE): number {
        switch (state) {
            case ASSEMBLY_STATE.PREPARING:
                return 0;
            case ASSEMBLY_STATE.AGENDA_PUBLISHED:
                return 1;
            case ASSEMBLY_STATE.DATE_PUBLISHED:
                return 5;
            case ASSEMBLY_STATE.PROTOCOL_PUBLISHED:
                return 6;
            case ASSEMBLY_STATE.CLOSED:
                return 9;
            default:
                throw new Error(`Unknown state: ${state}`);
        }
    }

    mapAssemblyRealestates(item: GetAssemblyByAssemblyId_assemblyitems): AssemblyRealestate[] {
        // A soft-deleted realestate makes the live assemblyrealestates.realestate join return null (for every
        // assembly state), so fall back to v_assemblyrealestates (matched by id) to still show the historical
        // realestate, marked read-only and with a "(gelöscht)" suffix since it no longer really exists.
        const closedById = new Map(item.assemblyrealestatesClosed.map((closedAr) => [closedAr.id, closedAr]));

        return item.assemblyrealestates.flatMap((ar): AssemblyRealestate[] => {
            if (ar.realestate) {
                return [
                    {
                        id: ar.id,
                        realestate: {
                            id: ar.realestate.id,
                            number: ar.realestate.number,
                            name: ar.realestate.name,
                            consolidationid: ar.realestate.consolidationid ?? null,
                            houses: ar.realestate.houses.map(
                                (house): House => ({
                                    zip: house.zip || "",
                                    city: house.city || "",
                                    street: house.street || "",
                                    housenumber: house.housenumber || ""
                                })
                            )
                        }
                    }
                ];
            }

            const closedRealestate = closedById.get(ar.id)?.realestate[0];
            if (!closedRealestate) {
                return [];
            }

            return [
                {
                    id: ar.id,
                    isReadOnly: true,
                    realestate: {
                        id: closedRealestate.id ?? "",
                        number: closedRealestate.number ?? "",
                        name: `${closedRealestate.name ?? ""} (${i18next.t("labels.deleted", { defaultValue: "gelöscht" })})`,
                        consolidationid: closedRealestate.consolidationid ?? null,
                        houses: closedRealestate.houses.map(
                            (house): House => ({
                                zip: house.zip || "",
                                city: house.city || "",
                                street: house.street || "",
                                housenumber: house.housenumber || ""
                            })
                        )
                    }
                }
            ];
        });
    }

    saveCurrentAssembly = async (curretAssemblyEditFormFields: AssemblyDetailEditFormFields, assemblyid: string) => {
        try {
            const upsertAssemblyDetails: any = {
                ...curretAssemblyEditFormFields,
                id: assemblyid,
                state: this.mapStateToInteger(curretAssemblyEditFormFields.state)
            };

            await this.upsertAssemblyMutation(upsertAssemblyDetails);

            if (this.currentAssemblyRealestatesHaveChanged) {
                const addingAssemblyRealestates = this.currentAssemblyRealestates;

                const insertAssemblyRealestateObjects: cond_assemblyrealestates_insert_input[] =
                    addingAssemblyRealestates.map((assemblyRealestate) => ({
                        id: assemblyRealestate.id,
                        assemblyid: assemblyid,
                        realestateid: assemblyRealestate.realestate.id
                    }));

                await this.insertAssemblyRealestateMutation(assemblyid, insertAssemblyRealestateObjects);
            }
        } catch (error: any) {
            this.printStatusMessage(i18next.t("screens.realestate.assembly.edit.save_error"), MessageType.ERROR);
        }
    };

    upsertAssemblyMutation = async (upsertAssemblyDetails: any) => {
        try {
            await apolloClientInstance.mutate<UpsertAssembly, UpsertAssemblyVariables>({
                mutation: UPSERT_ASSEMBLIES,
                variables: {
                    objects: upsertAssemblyDetails
                }
            });
        } catch (error: any) {
            this.printStatusMessage(i18next.t("screens.realestate.assembly.edit.save_error"), MessageType.ERROR);
        }
    };

    insertAssemblyRealestateMutation = async (
        assemblyid: string,
        insertAssemblyRealestateObjects: cond_assemblyrealestates_insert_input[]
    ) => {
        const validInsertBody = insertAssemblyRealestateObjects.filter(
            (obj) => obj.realestateid !== undefined && obj.realestateid !== null && obj.realestateid !== ""
        );

        if (validInsertBody.length === 0) {
            this.printStatusMessage(i18next.t("screens.realestate.assembly.no_realestate_warning"), MessageType.ERROR);
            return;
        }

        try {
            await apolloClientInstance
                .mutate<InsertAssemblyRealestateManager, InsertAssemblyRealestateManagerVariables>({
                    mutation: INSERT_ASSEMBLY_REALESTATE_MANAGER,
                    variables: { assemblyid: assemblyid, objects: validInsertBody }
                })
                .finally(() => this.setCurrentAssemblyRealestatesHaveChanged(false));
        } catch (error: any) {
            this.printStatusMessage(
                i18next.t("screens.realestate.assembly.edit.save_realesatete_error"),
                MessageType.ERROR
            );
        }
    };

    deleteAssemblyById = async (assemblyId: string | undefined) => {
        try {
            if (assemblyId !== undefined) {
                await apolloClientInstance.mutate<DeleteAssemblyById, DeleteAssemblyByIdVariables>({
                    mutation: DELETE_ASSEMBLY_BY_ID,
                    variables: {
                        assemblyid: assemblyId
                    }
                });

                this.printStatusMessage(
                    i18next.t("screens.realestate.assembly.edit.delete_success"),
                    MessageType.SUCCESS
                );

                this.setIsSuccessfullyDeletedAssembly(true);
            }
        } catch (error: any) {
            this.setIsSuccessfullyDeletedAssembly(false);
            this.printStatusMessage(i18next.t("screens.realestate.assembly.edit.delete_failed"), MessageType.ERROR);
        }
    };

    startIsAssemblyAssignedToRealestateSubscription(realestateid: string, assemblyid: string) {
        if (this.isAssemblyAssignedToRealestateSubscription) {
            this.stopIsAssemblyAssignedToRealestateSubscription();
        }
        if (realestateid && realestateid !== "" && assemblyid && assemblyid !== "") {
            const assemblyAssignedToRealestateSubscription = apolloClientInstance
                .subscribe<IsAssemblyAssignedToRealestate, IsAssemblyAssignedToRealestateVariables>({
                    query: SUBCRIPTION_IS_ASSEMBLY_ASSIGNED_TO_REALESTATE,
                    variables: { assemblyid: assemblyid, realestateid: realestateid }
                })
                .subscribe({
                    next: ({ data }) => {
                        if (data?.assemblyByRealestate && data.assemblyByRealestate.assigned) {
                            this.onIsAssemblyAssignedToRealestateSubscriptionDataReceived(
                                data.assemblyByRealestate.assigned.count
                            );
                        }
                    },
                    error: () => {
                        // Error is logged by Apollo errorLink
                        this.stopIsAssemblyAssignedToRealestateSubscription();
                    }
                });
            this.setIsAssemblyAssignedToRealestateSubscription(assemblyAssignedToRealestateSubscription);
        }
    }

    stopIsAssemblyAssignedToRealestateSubscription() {
        if (this.isAssemblyAssignedToRealestateSubscription) {
            this.isAssemblyAssignedToRealestateSubscription.unsubscribe();
            this.setIsAssemblyAssignedToRealestateSubscription(undefined);
        }
    }

    startSubscriptionHasAssemblyLiveModeChanged(assemblyid: string) {
        if (this.hasAssemblyLiveModeChangedSubscription) {
            this.stopSubscriptionHasAssemblyLiveModeChanged();
        }

        if (assemblyid && assemblyid !== "") {
            const assemblyStateChangedSubscription = apolloClientInstance
                .subscribe<HasAssemblyLiveModeChanged, HasAssemblyLiveModeChangedVariables>({
                    query: SUBCRIPTION_HAS_ASSEMBLY_LIVEMODE_CHANGED,
                    variables: { assemblyid: assemblyid }
                })
                .subscribe({
                    next: ({ data }) => {
                        if (data?.cond_assemblys && data.cond_assemblys.length > 0) {
                            this.setAssembyLiveModeHasStopped(!data.cond_assemblys[0].livemode);
                        }
                    },
                    error: () => {
                        // Error is logged by Apollo errorLink
                        this.stopIsAssemblyAssignedToRealestateSubscription();
                    }
                });

            this.sethasAssemblyLiveModeChangedSubscription(assemblyStateChangedSubscription);
        }
    }

    stopSubscriptionHasAssemblyLiveModeChanged() {
        if (this.hasAssemblyLiveModeChangedSubscription) {
            this.hasAssemblyLiveModeChangedSubscription.unsubscribe();
            this.sethasAssemblyLiveModeChangedSubscription(undefined);
            this.setAssemblyIsPreparing(false);
        }
    }

    loadAllAssemblies = async (realestateid: string | undefined, isManagerRealestateAssemblyRoute: boolean = false) => {
        try {
            this.setIsManagerRealestateAssemblyRoute(isManagerRealestateAssemblyRoute);

            const { data }: { data: GetAllAssemblies } = await apolloClientInstance.query<GetAllAssemblies>({
                query:
                    realestateid === "" || realestateid === undefined
                        ? GET_ALL_ASSEMBLIES
                        : isManagerRealestateAssemblyRoute
                          ? GET_ASSEMBLIES_BY_REALESTATEID
                          : GET_ALL_ASSEMBLIES_BY_COWWNER_REALESTATEID,
                fetchPolicy: "no-cache",
                variables:
                    realestateid === "" || realestateid === undefined
                        ? { userId: this.rootStore.authStore.user?.userid, state_lt: 90 }
                        : { realestateid: realestateid ?? "" }
            });

            runInAction(() => {
                this.assemblies = data.assemblyitems.map((item) => ({
                    id: item.id,
                    date: item.date,
                    state: this.mapState(item.state),
                    street: item.street ?? "",
                    housenumber: item.housenumber ?? "",
                    zip: item.zip ?? "",
                    city: item.city ?? "",
                    title: item.title,
                    locationdetail: item.locationdetail ?? "",
                    publishdate: item.publishdate,
                    motiondeadline: item.motiondeadline,
                    protocolauthor: item.protocolauthor ?? "",
                    protocoldate: item.protocoldate ?? "",
                    protocolplace: item.protocolplace ?? "",
                    livemode: item.livemode,
                    header: item.header ?? "",
                    footer: item.footer ?? "",
                    summaryassemblyfilesCount: item.summaryassemblyfiles?.aggregate?.count ?? 0,
                    assemblyrealestates: item.assemblyrealestates.map((ar) => ({
                        id: ar.id,
                        realestate: {
                            id: ar.realestate ? ar.realestate.id : "",
                            number: ar.realestate ? ar.realestate.number : "",
                            name: ar.realestate ? ar.realestate.name : ""
                        }
                    }))
                }));

                this.assemblyStates = this.rootStore.enumerationsStore
                    .getEnumByType(ENUM_TYPE.ASSEMBLY_STATES)
                    .map((item) => ({
                        id: item.id,
                        key: item.key ?? 0,
                        label: item.label ?? "Unknown"
                    }));
            });
        } catch (error) {
            console.error("Error loading assemblies:", error);
        }
    };

    loadCurrentAssembly = async (assemblyId: string, isBackgroundRefresh: boolean = false) => {
        this.setIsEditing(false);
        if (!isBackgroundRefresh) {
            this.setHasCurrentAssemblyLoaded(false);
        }

        /* LOAD ASSEMBLY FROM SERVER */
        if (!isBackgroundRefresh) {
            this.setLoadingType(LOADING_TYPE.ASSEMBLY);
        }

        const { data }: { data: GetAssemblyByAssemblyId } = await apolloClientInstance.query<
            GetAssemblyByAssemblyId,
            GetAssemblyByAssemblyIdVariables
        >({
            query: GET_ASSEMBLY_BY_ID,
            fetchPolicy: "no-cache",
            variables: {
                assemblyid: assemblyId
            }
        });

        if (data.assemblyitems.length === 0) {
            if (!isBackgroundRefresh) {
                this.setLoadingType(LOADING_TYPE.NONE);
                this.setHasCurrentAssemblyLoaded(true);
            }
            this.setIsNewAssembly(true);
            return;
        }

        if (data.assemblyitems.length > 0) {
            this.setHasActiveCoowners(undefined, data.assemblyitems[0].count_coowners?.hascoowner || false);
        }

        this.setAgendaItemsExists((data.assemblyitems[0].agendaitemCount.aggregate?.count ?? 0) > 0);

        runInAction(() => {
            // Map assemblyitems to the desired shape
            this.currentAssembly = data.assemblyitems.map(
                (item): Assembly => ({
                    id: item.id,
                    date: item.date,
                    state: this.mapState(item.state), // Use the mapState helper
                    street: item.street ?? null,
                    housenumber: item.housenumber ?? null,
                    zip: item.zip ?? null,
                    city: item.city ?? null,
                    title: item.title,
                    locationdetail: item.locationdetail ?? null,
                    publishdate: item.publishdate,
                    motiondeadline: item.motiondeadline,
                    livemode: item.livemode,
                    header: item.header ?? "",
                    footer: item.footer ?? "",
                    assemblyevents:
                        item.assemblyevents && item.assemblyevents.length > 0 ? item.assemblyevents[0].inserted : null,
                    protocolauthor: item.protocolauthor ?? null,
                    protocoldate: item.protocoldate ?? null,
                    protocolplace: item.protocolplace ?? null,
                    summaryassemblyfilesCount: item.summaryassemblyfiles.aggregate?.count ?? 0,
                    assemblyrealestates: this.mapAssemblyRealestates(item)
                })
            )[0]; // Assuming you want the first assembly item

            // Map assemblystates to a state list
            this.assemblyStates = this.rootStore.enumerationsStore
                .getEnumByType(ENUM_TYPE.ASSEMBLY_STATES)
                .map((item) => ({
                    id: item.id,
                    key: item.key ?? 0,
                    label: item.label ?? "Unknown"
                }));

            this.loadCurrentAssemblyRealestates();
        });
        // TODO: implements initializeFormFields() same as in TicketStore
        // this.initializeFormFields(this.currentAssembly);

        // this.setIsNewAssembly(this.currentAssembly === undefined);

        if (!isBackgroundRefresh) {
            this.setLoadingType(LOADING_TYPE.NONE);
        }

        if (!isBackgroundRefresh) {
            this.setHasCurrentAssemblyLoaded(true);
        }
    };

    loadCurrentAssemblyRealestates = async () => {
        this.currentAssemblyRealestates =
            this.currentAssembly === undefined
                ? []
                : this.currentAssembly?.assemblyrealestates.map((ar) => ({
                      id: ar.id,
                      isReadOnly: ar.isReadOnly,
                      realestate: {
                          id: ar.realestate.id,
                          number: ar.realestate.number,
                          name: ar.realestate.name,
                          consolidationid: ar.realestate.consolidationid ?? null,
                          houses: ar.realestate.houses?.map(
                              (house): House => ({
                                  zip: house.zip || "",
                                  city: house.city || "",
                                  street: house.street || "",
                                  housenumber: house.housenumber || ""
                              })
                          )
                      }
                  }));

        this.setFormattedRealestates(
            this.currentAssemblyRealestates.map((item) => ({
                id: item.realestate.id,
                name: item.realestate.name,
                number: item.realestate.number,
                consolidationid: item.realestate.consolidationid ?? ""
            }))
        );
    };

    setCurrentAssemblyRealestates = (assemblyRealestateList: AssemblyRealestate[]) => {
        this.currentAssemblyRealestates = assemblyRealestateList;
        this.setCurrentAssemblyRealestatesHaveChanged(true);
    };

    setCurrentAssemblyRealestatesHaveChanged = (currentAssemblyRealestatesHaveChanged: boolean) => {
        this.currentAssemblyRealestatesHaveChanged = currentAssemblyRealestatesHaveChanged;
    };

    setIsAssemblyAssignedToRealestate = (isAssemblyAssignedToRealestate: boolean) => {
        this.isAssemblyAssignedToRealestate = isAssemblyAssignedToRealestate;
    };

    addCurrentAssemblyRealestates = (assemblyRealestate: AssemblyRealestate) => {
        const exists = this.currentAssemblyRealestates.some(
            (item) => item.realestate.id === assemblyRealestate.realestate.id
        );

        if (!exists) {
            const updatedList = [...this.currentAssemblyRealestates, assemblyRealestate];
            this.setCurrentAssemblyRealestates(updatedList);
            this.setCurrentAssemblyRealestatesHaveChanged(true);
        }
    };

    removeCurrentAssemblyRealestates = (realestateId: string) => {
        const updatedList = this.currentAssemblyRealestates.filter(
            (realestate) => realestate.realestate.id !== realestateId
        );
        this.setCurrentAssemblyRealestates(updatedList);
    };

    reset = () => {
        this.currentAssembly = undefined;
    };

    setFormattedRealestates = (formattedRealestates: RealestateCondominium[]) => {
        this.formattedRealestates = formattedRealestates;
    };

    setCurrentAssembly = (assembly: Assembly) => {
        this.currentAssembly = assembly;
    };

    setCurrentAssemblyTitle = (currentAssemblyTitle: string) => {
        this.currentAssemblyTitle = currentAssemblyTitle;
    };

    setCurrentAssemblyLiveMode = (isLiveMode: boolean) => {
        if (this.currentAssembly) {
            this.currentAssembly.livemode = isLiveMode;
        }
    };

    setIsNewAssembly = (isNewAssembly: boolean) => {
        this.isNewAssembly = isNewAssembly;
        if (isNewAssembly) {
            this.setCurrentAssemblyRealestates([]);
        }
    };

    setHasCurrentAssemblyLoaded = (hasCurrentAssemblyLoaded: boolean) => {
        this.hasCurrentAssemblyLoaded = hasCurrentAssemblyLoaded;
    };

    setIsEditing = (isEditing: boolean) => {
        this.isEditing = isEditing;
    };

    setLoadingType = (loadingType: LOADING_TYPE) => {
        this.loadingType = loadingType;
    };

    setLoadingError = (title?: string) => {
        if (!title || title === "") {
            this.error = undefined;
        } else {
            this.error = { title: title };
        }
    };

    setIsAssemblyInsertedEventLoaded = (isAssemblyInsertedEventLoaded: boolean) => {
        this.isAssemblyInsertedEventLoaded = isAssemblyInsertedEventLoaded;
    };

    setIsAddNewAgendaItem = (isAddNewAgendaItem: boolean) => {
        this.isAddNewAgendaItem = isAddNewAgendaItem;
    };

    setIsAddNewChildAgendaItem = (isAddNewChildAgendaItem: boolean) => {
        this.isAddNewChildAgendaItem = isAddNewChildAgendaItem;
    };

    setResetAgendaItems = (resetAgendaItems: boolean) => {
        this.resetAgendaItems = resetAgendaItems;
    };

    updateSortAgendaItemList = () => {
        this.agendaItems.forEach((item, index) => {
            item.sort = index + 1;

            if (item.agendaitemchild) {
                item.agendaitemchild.forEach((childItem, childIndex) => {
                    childItem.sort = childIndex + 1;
                });
            }
        });

        this.agendaItems.sort((a, b) => a.sort - b.sort);
    };

    setOriginalAgendaItems = (agendaItems: AgendaItem[]) => {
        this.originalAgendaItems = agendaItems;
    };

    setCurrentAgendaItems = (agendaItems: AgendaItem[]) => {
        this.agendaItems = agendaItems;
        this.updateSortAgendaItemList();
    };

    setUnsavedAgendaItems = (unsavedAgendaItems: AgendaItem[]) => {
        this.unsavedAgendaItems = unsavedAgendaItems;
    };

    setAgendaItemsHaveChanged = () => {
        let agendaItemsHaveChanged = false;
        if (this.agendaItems.length === this.originalAgendaItems.length) {
            this.agendaItems.forEach((agendaItem, index) => {
                const originalAgendaItem = this.originalAgendaItems[index];

                if (
                    agendaItem.id !== originalAgendaItem.id ||
                    agendaItem.text !== originalAgendaItem.text ||
                    agendaItem.description !== originalAgendaItem.description ||
                    agendaItem.agendaitemchild?.length !== originalAgendaItem.agendaitemchild?.length ||
                    agendaItem.agendaitemfiles.length !== originalAgendaItem.agendaitemfiles.length
                ) {
                    agendaItemsHaveChanged = true;
                } else if (agendaItem.agendaitemchild) {
                    for (let i = 0; i < agendaItem.agendaitemchild.length; i++) {
                        if (
                            agendaItem.agendaitemchild[i]?.id !== originalAgendaItem.agendaitemchild?.[i]?.id ||
                            agendaItem.agendaitemchild[i]?.text !== originalAgendaItem.agendaitemchild?.[i]?.text ||
                            agendaItem.agendaitemchild[i]?.description !==
                                originalAgendaItem.agendaitemchild?.[i]?.description ||
                            agendaItem.agendaitemchild[i].agendaitemfiles.length !==
                                originalAgendaItem.agendaitemchild[i].agendaitemfiles.length
                        ) {
                            agendaItemsHaveChanged = true;
                            break;
                        }
                    }
                }
            });
        } else {
            agendaItemsHaveChanged = true;
        }

        this.agendaItemsHaveChanged = agendaItemsHaveChanged;
    };

    setIsAgendaItemFilesExists = (isAgendaItemFilesExists: boolean) => {
        this.isAgendaItemFilesExists = isAgendaItemFilesExists;
    };

    setIsCopyAgendaItemsActive = (isCopyAgendaItemsActive: boolean) => {
        this.isCopyAgendaItemsActive = isCopyAgendaItemsActive;
    };

    setIsAddingDocumentsDisplayed = (isAddingDocumentsDisplayed: boolean) => {
        this.isAddingDocumentsDisplayed = isAddingDocumentsDisplayed;
    };

    setFocusedAgendaItemIdText = (focusedAgendaItemIdText: string) => {
        this.focusedAgendaItemIdText = focusedAgendaItemIdText;
    };

    setFocusedAgendaItem = (focusedAgendaItem?: AgendaItem) => {
        this.focusedAgendaItem = focusedAgendaItem;
    };

    setFocusedAgendaItemIdDescription = (focusedAgendaItemIdDescription: string) => {
        this.focusedAgendaItemIdDescription = focusedAgendaItemIdDescription;
    };

    setIsManagerRealestateAssemblyRoute = (isManagerRealestateAssemblyRoute: boolean) => {
        this.isManagerRealestateAssemblyRoute = isManagerRealestateAssemblyRoute;
    };

    setAgendaItemsExists = (agendaItemsExists: boolean) => {
        this.agendaItemsExists = agendaItemsExists;
    };

    addDeletedAgendaItemId = (deletedAgendaItemIds: string) => {
        this.deletedAgendaItemIds?.push(deletedAgendaItemIds);
    };

    setIfAgendaItemFocusOrDocumentExists = (ifAgendaItemFocusOrDocumentExists: boolean) => {
        this.ifAgendaItemFocusOrDocumentExists = ifAgendaItemFocusOrDocumentExists;
    };

    setAssemblyId = (assemblyId: string) => {
        this.assemblyId = assemblyId;
    };

    setIsAddAssemblyRealestateModalDisplayed = (isAddAssemblyRealestateModalDisplayed: boolean) => {
        this.isAddAssemblyRealestateModalDisplayed = isAddAssemblyRealestateModalDisplayed;
    };

    createInsertAssemblyAgendaitems = (language: string) => {
        const insertAssemblyAgendaitems: any = this.agendaItems.map((agendaItem) => {
            const agendaitemtempManager = agendaItem.agendaitemfiles.map((agendaitemfile) => {
                return {
                    id: agendaitemfile.id,
                    fileid: agendaitemfile.fileid,
                    roleid: 30,
                    visible: true
                };
            });
            const agendaitemtempCoowner = agendaItem.agendaitemfiles.map((agendaitemfile) => {
                return {
                    id: agendaitemfile.id,
                    fileid: agendaitemfile.fileid,
                    roleid: 32,
                    visible: true
                };
            });
            const newAgendaItemFilesList = agendaitemtempManager.concat(agendaitemtempCoowner);

            const newAgendaItemChild = agendaItem.agendaitemchild!.map((agendaitemchild) => {
                return {
                    id: agendaitemchild.id,
                    assemblyid: this.assemblyId,
                    sort: agendaitemchild.sort,
                    agendaitems_mls: {
                        data: {
                            language: language,
                            text: agendaitemchild.text,
                            description: agendaitemchild.description
                        },
                        on_conflict: {
                            constraint: "UNIQUE_agendaitem_agendaitem_mls",
                            update_columns: ["text", "language", "description"]
                        }
                    },
                    agendaitemfiles: {
                        data: newAgendaItemFilesList,
                        on_conflict: {
                            constraint: "PK_agendaitemfiles",
                            update_columns: []
                        }
                    }
                };
            });

            return {
                id: agendaItem.id,
                assemblyid: this.assemblyId,
                sort: agendaItem.sort,
                agendaitems_mls: {
                    data: {
                        language: language,
                        text: agendaItem.text,
                        description: agendaItem.description
                    },
                    on_conflict: {
                        constraint: "UNIQUE_agendaitem_agendaitem_mls",
                        update_columns: ["text", "language", "description"]
                    }
                },
                agendaitemfiles: {
                    data: newAgendaItemFilesList,
                    on_conflict: {
                        constraint: "PK_agendaitemfiles",
                        update_columns: []
                    }
                },
                agendaitems: {
                    data: newAgendaItemChild,
                    on_conflict: {
                        constraint: "PK_agendaitems",
                        update_columns: ["sort", "parentagendaitemid"]
                    }
                }
            };
        });

        this.insertAssemblyAgendaitems = insertAssemblyAgendaitems;
    };

    createInsertAgendaitemsTemplate = () => {
        const insertAgendaitemsTemplates: cond_agendaitemtemplates_insert_input[] = this.agendaItems.map(
            (agendaItem) => {
                const newAgendaItemTemplateChild = agendaItem.agendaitemchild!.map((agendaitemchild) => {
                    return {
                        id: agendaitemchild.id,
                        sort: agendaitemchild.sort,
                        agendaitemtemplates_mls: {
                            data: {
                                language: getLang(),
                                text: agendaitemchild.text,
                                description: agendaitemchild.description
                            },
                            on_conflict: {
                                constraint: "UNIQUE_agendaitemtemplate_mls",
                                update_columns: ["text", "description"]
                            }
                        }
                    };
                });

                return {
                    id: agendaItem.id,
                    sort: agendaItem.sort,
                    agendaitemtemplates_mls: {
                        data: {
                            language: getLang(),
                            text: agendaItem.text,
                            description: agendaItem.description
                        },
                        on_conflict: {
                            constraint: "UNIQUE_agendaitemtemplate_mls",
                            update_columns: ["text", "language", "description"]
                        }
                    },
                    subagendaitemtemplates: {
                        data: newAgendaItemTemplateChild,
                        on_conflict: {
                            constraint: "PK_agendaitemtemplates",
                            update_columns: ["sort", "parentagendaitemtemplateid"]
                        }
                    }
                };
            }
        );

        this.insertAgendaitemsTemplates = insertAgendaitemsTemplates;
    };

    setShowRefetchAssemblyData = (showRefetchAssemblyData: boolean) => {
        this.showRefetchAssemblyData = showRefetchAssemblyData;
    };

    setIsAgendaItemTemplate = (isAgendaItemTemplate: boolean) => {
        this.isAgendaItemTemplate = isAgendaItemTemplate;
    };

    setSelectedCopyAssemblyAgendaOption = (selectedCopyAssemblyAgendaOption: number) => {
        this.selectedCopyAssemblyAgendaOption = selectedCopyAssemblyAgendaOption;
    };

    setIsAssemblyFilesExists = (isAssemblyFilesExists: boolean) => {
        this.isAssemblyFilesExists = isAssemblyFilesExists;
    };

    setOldAssemblyWithGeneralDocuments = (oldAssemblyWithGeneralDocuments: boolean) => {
        this.oldAssemblyWithGeneralDocuments = oldAssemblyWithGeneralDocuments;
    };

    setAssembliesEditFormStatus = (newStatus: FORM_STATUS) => {
        this.assembliesEditFormStatus = newStatus;
    };

    setAssembliesEditFormHasDirtyFields = (hasDirtyFields: boolean) => {
        this.assembliesEditFormHasDirtyFields = hasDirtyFields;
    };

    setIsAssemblyPDFExists = (isAssemblyPDFExists: boolean) => {
        this.isAssemblyPDFExists = isAssemblyPDFExists;
    };

    setIsNewAssemblyPDFExists = (isNewAssemblyPDFExists: boolean) => {
        this.isNewAssemblyPDFExists = isNewAssemblyPDFExists;
    };

    generateAssemblyRealestatePDF = async (assemblyid: string, withInternalComment?: boolean): Promise<boolean> => {
        this.setIsNewAssemblyPDFExists(false);
        this.rootStore.agendaitemStore.setIsDownloadAgendaItemFiles(true);

        const internalcommentsBody = {
            withinternalcomments: withInternalComment ? true : false
        };

        try {
            const response = await Axios.getAuthInstance().post(
                NetworkConfig.generateassemblyrealestatepdf + assemblyid,
                internalcommentsBody,
                {
                    urlWithoutParams: NetworkConfig.generateassemblyrealestatepdf,
                    headers: {
                        "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                    }
                }
            );

            const result = response.data;
            this.rootStore.agendaitemStore.setAssemblyPDFId(JSON.parse(result.data));
            this.setIsNewAssemblyPDFExists(true);
            this.rootStore.agendaitemStore.setIsDownloadAgendaItemFiles(false);
            this.rootStore.servicesStore.setRefetchServicesData(true);
        } catch (error) {
            console.error("Error while trying to call generateAssemblyRealestatePDF: ", error);
        }

        return this.isNewAssemblyPDFExists;
    };

    setHasActiveCoowners = (countCoowner?: number, hasCoowner?: boolean) => {
        if (countCoowner !== undefined) {
            this.hasActiveCoowners = countCoowner > 0;
        } else if (hasCoowner !== undefined) {
            this.hasActiveCoowners = hasCoowner;
        }
    };

    setIsNewDescription = (isNewDescription: string) => {
        this.isNewDescription = isNewDescription;
    };

    setAssemblyState = (state: number) => {
        if (this.currentAssembly) {
            this.currentAssembly.state = this.mapState(state);
        }
    };

    setIsAssemblyGetNotified = (isAssemblyGetNotified: boolean) => {
        this.isAssemblyGetNotified = isAssemblyGetNotified;
    };

    setIsCloseLiveModeTabActive = (isCloseLiveModeTabActive: boolean) => {
        this.isCloseLiveModeTabActive = isCloseLiveModeTabActive;
    };

    setIsAttendanceLiveModeTabActive = (isAttendanceLiveModeTabActive: boolean) => {
        this.isAttendanceLiveModeTabActive = isAttendanceLiveModeTabActive;
    };

    updateAssemblyState = async (state: number, successMsg?: string) => {
        try {
            const previousState = this.currentAssembly?.state;
            const response = await apolloClientInstance.mutate<UpdateAssemblyState, UpdateAssemblyStateVariables>({
                mutation: UPDATE_ASSEMBLY_STATE,
                variables: {
                    assemblyid: this.currentAssembly?.id,
                    state: state
                }
            });

            if (response?.data) {
                this.setAssemblyState(state);
                if (successMsg) {
                    this.printStatusMessage(successMsg, MessageType.SUCCESS);
                } else if (previousState === ASSEMBLY_STATE.CLOSED) {
                    this.printStatusMessage(
                        i18next.t("screens.assemblies.communicationcontent.published_state.reactivate"),
                        state === 0 ? MessageType.WARNING : MessageType.SUCCESS
                    );
                } else {
                    this.printStatusMessage(
                        i18next.t("screens.assemblies.communicationcontent.published_state." + state),
                        state === 0 ? MessageType.WARNING : MessageType.SUCCESS
                    );
                }
            } else {
                console.warn("Mutation succeeded but no data returned.");
            }
        } catch (error: any) {
            console.error("Error updating assembly state:", error.message);
            this.printStatusMessage(i18next.t("screens.realestate.assembly.edit.save_error"), MessageType.ERROR);
        }
    };

    updateAssemblyLiveMode = async (livemode: boolean, successMsg?: string): Promise<boolean> => {
        try {
            const response = await apolloClientInstance.mutate<UpdateAssemblyLiveMode, UpdateAssemblyLiveModeVariables>(
                {
                    mutation: UPDATE_ASSEMBLY_LIVEMODE,
                    variables: {
                        assemblyid: this.currentAssembly?.id,
                        livemode: livemode
                    }
                }
            );

            if (response?.data) {
                if (this.currentAssembly) {
                    this.setCurrentAssemblyLiveMode(livemode);
                }

                if (successMsg) {
                    this.printStatusMessage(successMsg, MessageType.SUCCESS);
                }

                return true;
            } else {
                this.printStatusMessage("Live Modus konnte nicht gestartet werden", MessageType.ERROR);
                console.warn("Mutation succeeded but no data returned.");
                return false;
            }
        } catch (error: any) {
            console.error("Error set assembly livemode:", error.message);
            this.printStatusMessage(i18next.t("screens.realestate.assembly.edit.save_error"), MessageType.ERROR);
            return false;
        }
    };

    notifyAssembly = async (assemblyState: ASSEMBLY_STATE) => {
        try {
            this.setIsAssemblyGetNotified(true);
            const requestBody = {
                assemblyid: this.currentAssembly?.id || "",
                notificationtype: this.mapStateToInteger(assemblyState)
            };

            await Axios.getAuthInstance().put(NetworkConfig.insertNotificationUrl, requestBody, {
                headers: {
                    "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                }
            });

            this.setIsAssemblyGetNotified(false);
            // this.setShowRefetchAssemblyData(true);
            this.setIsAssemblyInsertedEventLoaded(false);
        } catch (error) {
            this.setIsAssemblyGetNotified(false);
            this.printStatusMessage(
                i18next.t("screens.realestate.news_preview.save_notification_error", {
                    errorMessage: error
                }),
                MessageType.ERROR
            );
        }
    };

    setIsSuccessfullyDeletedAssembly = (isSuccessfullyDeletedAssembly: boolean) => {
        this.isSuccessfullyDeletedAssembly = isSuccessfullyDeletedAssembly;
    };

    saveAssemblyHeaderFooter = async (): Promise<boolean> => {
        try {
            await apolloClientInstance.mutate<UpsertAssemblyHeaderFooter, UpsertAssemblyHeaderFooterVariables>({
                mutation: UPSERT_ASSEMBLY_HEADER_FOOTER,
                variables: {
                    assemblyid: this.currentAssembly?.id,
                    header: this.currentAssembly?.header ?? "",
                    footer: this.currentAssembly?.footer ?? ""
                }
            });
            return true;
        } catch (error) {
            console.error("Error set assembly header and footer:", error);
            return false;
        }
    };

    /**
     * Records the time of the closing in the conclusion of the protocol and closes the live mode.
     *
     * The note has to be written first: closing the live mode sets `livemode` to false, which makes
     * `LiveMode` render nothing at all. A note that failed afterwards would leave the assembly closed
     * without the timestamp documenting its end, and no screen left to retry on.
     */
    recordClosingTimeAndCloseLiveMode = async (closingTime: string): Promise<boolean> => {
        const isClosingNoteAdded = await this.addClosingNoteToConclusion(closingTime);

        if (!isClosingNoteAdded) {
            return false;
        }

        return this.updateAssemblyLiveMode(false, i18next.t("screens.assemblies.livemode.successfull_closed"));
    };

    /**
     * Appends the time the assembly was closed to the conclusion ("Schlussbemerkung") of the protocol.
     * The conclusion cannot be deleted, so the note stays available as proof of when the assembly ended.
     * The conclusion is restored when saving fails, so an unsaved note cannot be persisted later on by
     * an unrelated header/footer save.
     */
    addClosingNoteToConclusion = async (closingTime: string): Promise<boolean> => {
        if (!this.currentAssembly) {
            return false;
        }

        const closingNote = i18next.t("screens.assemblies.livemode.closing_note", {
            time: closingTime
        });
        const conclusionBeforeClosingNote = this.currentAssembly.footer;

        this.currentAssembly.footer = `${conclusionBeforeClosingNote ?? ""}<p>${closingNote}</p>`;

        const isConclusionSaved = await this.saveAssemblyHeaderFooter();

        if (!isConclusionSaved) {
            runInAction(() => {
                if (this.currentAssembly) {
                    this.currentAssembly.footer = conclusionBeforeClosingNote;
                }
            });

            this.printStatusMessage(
                i18next.t("screens.assemblies.livemode.closing_note_save_error"),
                MessageType.ERROR
            );

            return false;
        }

        return true;
    };

    setIsFreetextHeaderExpanded = (isFreetextHeaderExpanded: boolean) => {
        this.isFreetextHeaderExpanded = isFreetextHeaderExpanded;
    };

    setIsFreetextFooterExpanded = (isFreetextFooterExpanded: boolean) => {
        this.isFreetextFooterExpanded = isFreetextFooterExpanded;
    };
}
